import { useMemo, useState } from "react";

import { DateTime } from "luxon";
import {
  ArrowPathIcon,
  BoltIcon,
  ChevronDownIcon,
  ClockIcon,
  PauseIcon,
  PlayIcon,
  StopIcon,
} from "@heroicons/react/24/outline";

import Avatar from "../user/Avatar";
import Card from "../shared/Card/Card";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import {
  DismantlingSession,
  TimerStatus,
  formatElapsed,
  formatElapsedShort,
  useDismantlingTimer,
} from "../../utils/dismantlingTimers";

interface DismantlingTimerCardProps {
  jobId: string;
  /** Current logged-in Wilma user id, attached to newly-started sessions. */
  userId: string | null;
  /** Estimated dismantling time in minutes (from `IJob.duration`). */
  estimatedMinutes?: number | null;
  /**
   * Fires the first time a user presses Start for a job that has no sessions
   * yet. Used by ViewJob to set `job.startDate` once we go live.
   */
  onFirstStart?: (startedAt: string) => void;
  /**
   * Fires when the user presses Stop. Used by ViewJob to set `job.endDate`.
   */
  onStop?: (stoppedAt: string) => void;
  /** Fires when the user presses Reset — used to clear start/end on the job. */
  onReset?: () => void;
}

const STATUS_META: Record<
  TimerStatus,
  { label: string; className: string; dotClassName: string; clockClass: string }
> = {
  idle: {
    label: "Not started",
    className: "bg-slate-100 text-slate-600 ring-slate-200",
    dotClassName: "bg-slate-400",
    clockClass: "text-slate-500",
  },
  running: {
    label: "Running",
    className: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    dotClassName: "bg-emerald-500 animate-pulse",
    clockClass: "text-emerald-700",
  },
  paused: {
    label: "Paused",
    className: "bg-amber-50 text-amber-700 ring-amber-200",
    dotClassName: "bg-amber-500",
    clockClass: "text-amber-700",
  },
  stopped: {
    label: "Completed",
    className: "bg-slate-900 text-white ring-slate-900",
    dotClassName: "bg-white/80",
    clockClass: "text-slate-900",
  },
};

/**
 * Dismantling stopwatch for a single job. Lets a mechanic start, pause,
 * resume and stop a timer to record hands-on dismantling time. The total
 * elapsed time is the sum of every session's duration (pauses are excluded).
 *
 * Persistence lives in `localStorage` today — see `dismantlingTimers.ts`
 * and the `/job/:id/sessions` entry in `docs/pending-endpoints.md` for the
 * server-side story.
 */
export default function DismantlingTimerCard({
  jobId,
  userId,
  estimatedMinutes,
  onFirstStart,
  onStop,
  onReset,
}: DismantlingTimerCardProps) {
  const { timer, status, elapsedMs, start, pause, resume, stop, reset } =
    useDismantlingTimer(jobId);

  const [sessionsOpen, setSessionsOpen] = useState(false);
  const meta = STATUS_META[status];

  const sessionCount = timer.sessions.length;
  const estimatedMs = useMemo(
    () => (estimatedMinutes && estimatedMinutes > 0 ? estimatedMinutes * 60_000 : 0),
    [estimatedMinutes]
  );
  const overEstimate = estimatedMs > 0 && elapsedMs > estimatedMs;
  const progressPct =
    estimatedMs > 0
      ? Math.min(100, Math.round((elapsedMs / estimatedMs) * 100))
      : 0;

  const firstSession = timer.sessions[0] ?? null;
  const lastSession = timer.sessions[timer.sessions.length - 1] ?? null;

  const handleStart = () => {
    const isFirst = timer.sessions.length === 0;
    const startedAt = new Date().toISOString();
    start(userId);
    if (isFirst) onFirstStart?.(startedAt);
  };

  const handleResume = () => {
    resume(userId);
  };

  const handleStop = () => {
    const stoppedAt = new Date().toISOString();
    stop();
    onStop?.(stoppedAt);
  };

  const handleReset = () => {
    const ok = window.confirm(
      "Clear all recorded dismantling sessions for this job? The total time will be reset to 0."
    );
    if (!ok) return;
    reset();
    onReset?.();
  };

  return (
    <Card
      title="Dismantling Timer"
      subtitle="Start, pause and stop the clock to record hands-on dismantling time"
      bodyClassName="p-4 sm:p-5"
      headerRight={
        <span
          className={classNames(
            "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset",
            meta.className
          )}
        >
          <span
            className={classNames("h-1.5 w-1.5 rounded-full", meta.dotClassName)}
            aria-hidden="true"
          />
          {meta.label}
        </span>
      }
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Clock + controls — takes two of three columns so the digits have
            room to breathe and the buttons don't wrap. */}
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <ClockIcon className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                Elapsed
              </p>
              {sessionCount > 0 && (
                <p className="text-[11px] text-slate-500">
                  {sessionCount}{" "}
                  {sessionCount === 1 ? "session" : "sessions"}
                </p>
              )}
            </div>

            <p
              className={classNames(
                "mt-1 font-mono text-5xl font-semibold tabular-nums tracking-tight sm:text-6xl",
                meta.clockClass
              )}
            >
              {formatElapsed(elapsedMs)}
            </p>

            {estimatedMs > 0 && (
              <div className="mt-4 space-y-1">
                <div className="flex items-center justify-between text-[11px] text-slate-500">
                  <span className="flex items-center gap-1">
                    <BoltIcon className="h-3 w-3" aria-hidden />
                    Estimate {formatElapsedShort(estimatedMs)}
                  </span>
                  <span
                    className={classNames(
                      "tabular-nums",
                      overEstimate ? "font-semibold text-rose-600" : ""
                    )}
                  >
                    {overEstimate
                      ? `+${formatElapsedShort(elapsedMs - estimatedMs)} over`
                      : `${progressPct}% of estimate`}
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                  <div
                    className={classNames(
                      "h-full rounded-full transition-all",
                      overEstimate ? "bg-rose-500" : "bg-emerald-500"
                    )}
                    style={{
                      width: `${
                        overEstimate ? 100 : Math.max(progressPct, 2)
                      }%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {status === "idle" && (
              <TimerButton
                tone="primary"
                icon={PlayIcon}
                onClick={handleStart}
              >
                Start
              </TimerButton>
            )}
            {status === "running" && (
              <>
                <TimerButton tone="amber" icon={PauseIcon} onClick={pause}>
                  Pause
                </TimerButton>
                <TimerButton tone="rose" icon={StopIcon} onClick={handleStop}>
                  Stop
                </TimerButton>
              </>
            )}
            {status === "paused" && (
              <>
                <TimerButton
                  tone="primary"
                  icon={PlayIcon}
                  onClick={handleResume}
                >
                  Resume
                </TimerButton>
                <TimerButton tone="rose" icon={StopIcon} onClick={handleStop}>
                  Stop
                </TimerButton>
              </>
            )}
            {status === "stopped" && (
              <TimerButton
                tone="primary"
                icon={PlayIcon}
                onClick={handleResume}
              >
                Resume
              </TimerButton>
            )}

            {sessionCount > 0 && (
              <button
                type="button"
                onClick={handleReset}
                className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
              >
                <ArrowPathIcon className="h-4 w-4" aria-hidden />
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Summary column — started / last activity / sessions list. */}
        <div className="space-y-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Started
            </p>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {firstSession
                ? formatDateTime(
                    firstSession.startedAt,
                    DateTime.DATETIME_MED
                  )
                : "—"}
            </p>
            {firstSession?.userId && (
              <div className="mt-2 flex items-center gap-2">
                <Avatar
                  userId={firstSession.userId}
                  classes="h-6 w-6 rounded-full"
                  tooltipPrefix="Started by"
                />
                <span className="text-xs text-slate-500">First mechanic</span>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {status === "running" ? "Current session" : "Last activity"}
            </p>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {lastSession
                ? formatDateTime(
                    lastSession.endedAt ?? lastSession.startedAt,
                    DateTime.DATETIME_MED
                  )
                : "—"}
            </p>
            {timer.stoppedAt && (
              <p className="mt-1 text-[11px] text-slate-500">
                Stopped{" "}
                {formatDateTime(timer.stoppedAt, DateTime.DATETIME_MED)}
              </p>
            )}
          </div>

          {sessionCount > 0 && (
            <button
              type="button"
              onClick={() => setSessionsOpen((v) => !v)}
              className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300"
            >
              <span>
                {sessionsOpen ? "Hide" : "Show"} sessions ({sessionCount})
              </span>
              <ChevronDownIcon
                className={classNames(
                  "h-4 w-4 text-slate-400 transition-transform",
                  sessionsOpen ? "rotate-180" : ""
                )}
                aria-hidden
              />
            </button>
          )}
        </div>
      </div>

      {sessionsOpen && sessionCount > 0 && (
        <SessionsList sessions={timer.sessions} />
      )}
    </Card>
  );
}

interface TimerButtonProps {
  tone: "primary" | "amber" | "rose";
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onClick: () => void;
  children: React.ReactNode;
}

const TONE_CLASSES: Record<TimerButtonProps["tone"], string> = {
  primary:
    "border-transparent bg-primary-600 text-white shadow-sm hover:bg-primary-700 focus:ring-primary-500",
  amber:
    "border-transparent bg-amber-500 text-white shadow-sm hover:bg-amber-600 focus:ring-amber-400",
  rose:
    "border-transparent bg-rose-600 text-white shadow-sm hover:bg-rose-700 focus:ring-rose-500",
};

function TimerButton({ tone, icon: Icon, onClick, children }: TimerButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-1",
        TONE_CLASSES[tone]
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
      {children}
    </button>
  );
}

function SessionsList({ sessions }: { sessions: DismantlingSession[] }) {
  // Newest session first — that's what mechanics will look for when they
  // come back to the page after a break.
  const ordered = useMemo(() => [...sessions].reverse(), [sessions]);

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50">
          <tr>
            <th
              scope="col"
              className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500"
            >
              Started
            </th>
            <th
              scope="col"
              className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500"
            >
              Ended
            </th>
            <th
              scope="col"
              className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-slate-500"
            >
              Duration
            </th>
            <th
              scope="col"
              className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500"
            >
              Mechanic
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {ordered.map((s) => {
            const start = Date.parse(s.startedAt);
            const end = s.endedAt ? Date.parse(s.endedAt) : Date.now();
            const ms = Number.isFinite(start) && Number.isFinite(end) && end > start
              ? end - start
              : 0;
            const running = s.endedAt === null;
            return (
              <tr key={s.id}>
                <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                  {formatDateTime(s.startedAt, DateTime.TIME_SIMPLE)}
                  <span className="ml-1 text-[11px] text-slate-400">
                    {formatDateTime(s.startedAt, DateTime.DATE_MED)}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                  {running ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                      Running
                    </span>
                  ) : (
                    <>
                      {formatDateTime(s.endedAt!, DateTime.TIME_SIMPLE)}
                      <span className="ml-1 text-[11px] text-slate-400">
                        {formatDateTime(s.endedAt!, DateTime.DATE_MED)}
                      </span>
                    </>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-slate-900">
                  {formatElapsedShort(ms)}
                </td>
                <td className="px-3 py-2">
                  {s.userId ? (
                    <Avatar
                      userId={s.userId}
                      classes="h-6 w-6 rounded-full"
                      tooltipPrefix="Started by"
                    />
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
